import { ROUTES } from "@/constants/routes";
import { RailCanvasHeroPreviewTooltip } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailCanvasHeroPreviewTooltip";
import { RailChevron } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import { useRailRowMultiMenuGate } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import { RailRowOpenButton } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowOpenButton";
import {
  useRailRowSelection,
  useRegisterRailResource,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { useSingleOrDoubleClick } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useSingleOrDoubleClick";
import {
  ObjectActionMenu,
  useCanvasObjectActionHandlers,
  useObjectActions,
} from "@/object-actions";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import {
  getCanvasHeroImageUrl,
  RESOURCE_TAG_FAVORITE,
  type Canvas,
} from "@cohesive/api-client";
import { useThemeContext } from "@cohesive/ui-components";
import { Frame } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface WorkspacesRailCanvasRowProps {
  canvas: Canvas;
  active?: boolean;
  /** Rail section this row lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  onRename: (canvas: Canvas) => void;
  /** Open the "Move to workspace" dialog. */
  onMove?: (canvas: Canvas) => void;
  /** Open the canvas Share dialog (gated on can_manage_assignments). */
  onShare?: (canvas: Canvas) => void;
  onDelete?: (canvas: Canvas) => void;
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  /**
   * When set, single-clicking the title selects AND toggles the canvas's
   * nested chats + files (a leading always-visible chevron is an instant
   * toggle too); double click opens. Omitted for flat lists (favorites /
   * shared), where there is nothing to expand and title click only selects.
   */
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

export function WorkspacesRailCanvasRow({
  canvas,
  active,
  section = "canonical",
  onRename,
  onMove,
  onShare,
  onDelete,
  dragRef,
  dragProps,
  isDragging,
  expanded,
  onToggleExpanded,
}: WorkspacesRailCanvasRowProps) {
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const menu = useRailRowContextMenu();
  const label = canvas.name || "Untitled";
  const isFavorite = !!canvas.tags?.includes(RESOURCE_TAG_FAVORITE);
  // The preview must drop the moment the user opens the row: Radix's own
  // close-on-pointerdown never fires for the Open pill (it stops pointerdown
  // so the row's drag doesn't arm), and `active` — which suppresses the
  // preview of the open page — only flips once navigation lands. Re-entering
  // the row re-arms the preview (a no-longer-active row can be hovered again).
  const [previewDismissed, setPreviewDismissed] = useState(false);
  // Parent CanvasRowDropZone is the list-item host for contiguous chrome.
  const { selected, onActivate, rowKey, selectionAttrs } = useRailRowSelection(
    canvas.id,
    section,
    !!active,
    "canvas",
    false,
  );

  const registered = useMemo(
    () => ({ kind: "canvas" as const, data: canvas }),
    [canvas],
  );
  useRegisterRailResource("canvas", canvas.id, registered);

  const onContextMenu = useRailRowMultiMenuGate(rowKey, menu.onContextMenu);

  // Title click selects and, when expandable, also toggles the nested
  // contents — one gesture set with the workspace/folder rows. Modifier
  // clicks are multi-select gestures and must not toggle. Double click
  // opens; the delay (expandable rows only) keeps it from flipping the row
  // open and shut on the way.
  const titleClicks = useSingleOrDoubleClick(
    (e) => {
      onActivate(e);
      if (!e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
        onToggleExpanded?.();
      }
    },
    (e) => {
      onActivate(e);
      setPreviewDismissed(true);
      navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
    },
    { delayed: !!onToggleExpanded },
  );

  const canShare = !!onShare;
  const canDelete = !!onDelete;

  const handlers = useCanvasObjectActionHandlers({
    canvas,
    canShare,
    canDelete,
  });

  const { actions } = useObjectActions({
    kind: { type: "canvas" },
    surface: "left-rail",
    enabled: menu.open,
    state: {
      isFavorite,
      canShare,
      canDelete,
      canEdit: true,
    },
    handlers: {
      ...handlers,
      // Mirror the row click: activate rail selection before navigating so the
      // favorites/canonical highlight stays in sync when opening via the menu.
      open: () => {
        onActivate();
        setPreviewDismissed(true);
        navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
      },
      rename: () => window.setTimeout(() => onRename(canvas), 0),
      move: onMove
        ? () => window.setTimeout(() => onMove(canvas), 0)
        : undefined,
      share: onShare
        ? () => window.setTimeout(() => onShare(canvas), 0)
        : undefined,
      delete: onDelete
        ? () => window.setTimeout(() => onDelete(canvas), 0)
        : undefined,
    },
  });

  return (
    // The whole row is the preview trigger: the preview's position then keys
    // off the row's full width (→ the rail's right edge), never overlapping
    // the trailing Open pill / kebab the title button stops short of.
    <RailCanvasHeroPreviewTooltip
      label={label}
      imageUrl={getCanvasHeroImageUrl(canvas, mode)}
      active={active}
      suppress={menu.open || previewDismissed}
    >
      <div
        ref={dragRef}
        data-rail-canvas-row
        {...selectionAttrs}
        onContextMenu={onContextMenu}
        onPointerEnter={() => setPreviewDismissed(false)}
        className={cn(
          "group/row relative flex min-h-7 w-full items-center gap-2.5 px-2 pr-8 hover:bg-n-05",
          !selected && "rounded-md",
          selected && "bg-foreground/[0.04] text-n-90",
          isDragging && "cursor-grabbing opacity-40",
        )}
        {...dragProps}
      >
        {onToggleExpanded && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse canvas" : "Expand canvas"}
            title={expanded ? "Collapse" : "Expand"}
            className="-m-1 inline-flex shrink-0 rounded-sm p-1 hover:bg-n-10"
          >
            <RailChevron open={expanded} />
          </button>
        )}
        <button
          type="button"
          // Single click selects + toggles (cmd/shift for multi-select only);
          // double click opens. The chevron stays as an instant toggle.
          onClick={titleClicks.onClick}
          onDoubleClick={titleClicks.onDoubleClick}
          aria-expanded={onToggleExpanded ? expanded : undefined}
          /**
           * Don't take focus on mousedown: the default blurs the composer, so
           * its focus ring would drop here and spring back once a double-click
           * open refocused it. Click still fires on mouseup; keyboard is
           * untouched. Same reasoning as the workspace row's title button.
           */
          onMouseDown={(e) => e.preventDefault()}
          className={cn(
            // self-stretch: keep the full row height clickable.
            "flex min-w-0 flex-1 select-none items-center gap-2.5 self-stretch text-left outline-none focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
            // Tighten the chevron→icon gap to match RailDisclosureIcon's
            // gap-1.5 (the row's gap-2.5 minus 4px).
            onToggleExpanded && "-ml-1",
          )}
        >
          <Frame className="size-4 shrink-0 text-n-50" />
          <span className="min-w-0 flex-1 truncate text-base text-n-80">
            {label}
          </span>
        </button>

        {/* Mirrors the double-click gesture: select, then open. */}
        <RailRowOpenButton
          label={label}
          onOpen={() => {
            onActivate();
            setPreviewDismissed(true);
            navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
          }}
        />

        <ObjectActionMenu
          actions={actions}
          kind={{ type: "canvas" }}
          name={label}
          menuController={menu}
          label={label}
        />
      </div>
    </RailCanvasHeroPreviewTooltip>
  );
}
